/**
 * Hook: GET /api/t/{slug}/candidates — paginated candidate list.
 *
 * Cache key: ``['candidates', tenantSlug, { jdId, status }]`` (Architect §4).
 * 30c consumes with different ``status`` filters; 30b consumes with default
 * filters.
 *
 * P0-jd-pipeline-tenant-wide-leak (2026-05-20): when ``opts.jdId`` is set,
 * ``jd_id=`` is forwarded to the backend so results are scoped to the JD's
 * pairings (per-JD pipelines, project_jd_candidate_model.md Option C).
 * Without ``jdId``, the request returns the tenant-wide list.
 *
 * Response envelope is ``{ items, next_cursor, has_more }`` per
 * ``app/api/candidates.py`` ``list_candidates_endpoint``. v1 of this hook
 * fetches the first page only — pagination wires in 30c.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { CandidateStatus } from "@/components/candidates/StatusPill";

/**
 * Mirrors the backend `RankOneStatus` literal (app/schemas/candidates.py).
 *
 * "queued" / "running" are the non-terminal states added by migration 0113,
 * when the initial rank-one moved off the request thread onto the durable AI
 * worker. The original three keep their exact meanings, so this is an additive
 * extension — no existing branch changes behavior.
 */
export type RankOneStatus =
  | "complete"
  | "failed"
  | "pending"
  | "queued"
  | "running";

/**
 * The non-terminal rank-one states — mirrors the backend's
 * `RANK_ONE_IN_FLIGHT` (app/schemas/candidates.py), which is pinned to the
 * backend literal by a unit test. Keep the two in sync: a value the backend
 * treats as in-flight but this omits would resolve a spinner early, onto a
 * candidate that has no fit score yet.
 *
 * Lives here rather than beside the poll hook so both this module and
 * `useRankOnePolling` can read it without an import cycle.
 */
export function isRankOneInFlight(
  status: RankOneStatus | null | undefined,
): boolean {
  return status === "queued" || status === "running";
}

/**
 * List-level poll cadence. Slower than the per-row upload poll (2500ms): this
 * refetches a whole page of candidates, and nobody is watching a given row as
 * closely once they have left the upload screen.
 */
const LIST_POLL_INTERVAL_MS = 4000;

/**
 * Page size requested from the list endpoint. Pinned to the backend's
 * ``_MAX_LIMIT`` (app/api/candidates.py) so a JD with more than the default
 * 25 paired candidates loads in full — CandidatesV2 pages client-side over
 * this set, so without it everything past row 25 was silently hidden with no
 * load-more. Raising the limit is the minimal, demo-safe fix (no cursor
 * pagination); the backend caps at ``_MAX_LIMIT`` regardless.
 */
const CANDIDATES_PAGE_LIMIT = 100;

export interface Candidate {
  id: string;
  tenant_id: string;
  created_by: string;
  name: string;
  // B-07.3 — contact fields surfaced above the résumé on the candidate detail
  // page (founder override OQ-2). Null/absent when the candidate has no value on
  // file; the FE hides the corresponding line rather than rendering "Email: —".
  // Optional (like the other late-added serializer fields) so older fixtures and
  // partial-deploy envelopes degrade gracefully.
  email?: string | null;
  phone?: string | null;
  linkedin_url: string | null;
  text: string | null;
  resume_html: string | null;
  filename: string | null;
  status: CandidateStatus;
  is_sample: boolean;
  sample_photo_id: number | null;
  storage_key_present: boolean;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
  // Phase 2.7.1 — async resume-format state. TRUE while the worker is
  // processing; FALSE once the worker has persisted HTML (success path)
  // or flipped failed_at (failure / sweep path). The four FE render states
  // for the Resume tab are driven off these two columns + resume_html + text.
  resume_formatting?: boolean;
  resume_formatting_failed_at?: string | null;
  // RANKING-404 Fix 3 — rank-one outcome on POST /candidates response.
  // "complete" = ranked; "failed" = AI/parse error (Retry CTA); "pending" =
  // rank-one not attempted (no JD / no credits).
  //
  // Migration 0113 made this a real `candidates` column and added the two
  // non-terminal states ("queued" / "running") when the initial rank-one moved
  // off the request thread onto the durable AI worker. Both mean "still
  // analyzing" — poll until the value settles (see `useRankOnePolling`).
  //
  // Because it is now a column it is populated on the GET endpoints too, which
  // is what makes polling possible. Null still means rank-one never applied to
  // this candidate (no JD / no résumé text), including rows predating 0113.
  rank_one_status?: RankOneStatus | null;
  // P1-candidate-pdf-extract-error-toast — text extraction outcome on
  // POST /candidates (and PUT /candidates/{id} with a resume). "ok" = text
  // extracted; "failed" = extraction failed; "skipped" = MIME not extracted.
  // Null on GET endpoints and JSON-only creates.
  text_extract_status?: "ok" | "failed" | "skipped" | null;
  // D5 — headline fit: fit_overall from the candidate's routed-jd_id ranking
  // (fallback most-recent). Single source of truth for the candidate's fit
  // LABEL across surfaces. Populated on GET /candidates (list); null/absent
  // elsewhere or when the candidate has no ranking. The All-Candidates table
  // prefers THIS over the selected-JD ranking decoration so the label matches
  // the JD-centric Dashboard.
  fit_overall?: "strong" | "moderate" | "weak" | null;
}

interface CandidatesPage {
  items: Candidate[];
  next_cursor: string | null;
  has_more: boolean;
}

interface UseCandidatesOpts {
  jdId?: string;
  status?: CandidateStatus;
}

export function useCandidates(
  opts: UseCandidatesOpts = {},
): UseQueryResult<Candidate[], ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const jdId = opts.jdId ?? null;
  const status = opts.status ?? null;

  return useQuery<Candidate[], ApiError>({
    queryKey: ["candidates", tenantSlug, { jdId, status }],
    queryFn: async () => {
      const params: Record<string, string> = {
        limit: String(CANDIDATES_PAGE_LIMIT),
      };
      if (status) params.status = status;
      if (jdId) params.jd_id = jdId;
      const r = await apiClient.get<CandidatesPage>(
        `/api/t/${tenantSlug}/candidates`,
        { params },
      );
      return r.data.items;
    },
    enabled: !!tenantSlug,
    staleTime: 30_000,
    // Resolve just-uploaded candidates in place (migration 0113). The upload
    // page auto-redirects here ~5s after the last POST, which is often BEFORE
    // the worker has finished ranking — so without this the list would sit on
    // "Untitled candidate" + an em-dash fit until a manual refresh, which is
    // exactly the #441 bug from the user's side of the glass.
    //
    // Self-limiting: the predicate polls only while some row on the page is
    // actually mid-flight and returns false the moment they all settle, so a
    // steady-state list issues no extra requests. Deliberately NOT
    // `refetchIntervalInBackground` (unlike the per-row poll): a backgrounded
    // list has nobody watching it, and this is a whole-page fetch.
    refetchInterval: (query) => {
      const items = query.state.data;
      if (!items) return false;
      return items.some((c) => isRankOneInFlight(c.rank_one_status))
        ? LIST_POLL_INTERVAL_MS
        : false;
    },
  });
}
